import type { ChapterImage, Sentence } from "../api";
import type { Line } from "./sentences";

/**
 * Where a chapter's pictures go in text that has already been cut into lines
 * (ADR-0038).
 *
 * The server anchors a picture to a paragraph and hands back both the
 * paragraph's index and the byte offset just past it. Which of the two is
 * usable depends on what the page is showing:
 *
 *   - the author's text, split into lines here → the paragraph index;
 *   - a narrated transcript, whose sentences carry byte offsets into the
 *     revision they were recorded from → the offset.
 *
 * Both produce the same answer: the picture follows the last line that belongs
 * to its paragraph. Unplaced pictures appear in neither — their paragraph is
 * gone, and a reader is shown a chapter, not a repair job.
 */

/** TOP is the key for pictures that open the chapter, before any line. */
export const TOP = -1;

export type FigurePlan = Map<number, ChapterImage[]>;

/**
 * planByParagraph maps a line index to the pictures that follow it.
 *
 * A picture goes after the *last* line of its paragraph: a paragraph is
 * several sentences, each its own pressable line, and a picture in the middle
 * of one would split a sentence from the rest of its paragraph.
 */
export function planByParagraph(lines: Line[], images: ChapterImage[]): FigurePlan {
  // The last line index of each paragraph, counting paragraphs the way the
  // server does — non-blank lines, with a new one at each opensParagraph.
  const lastLineOf: number[] = [];
  let paragraph = -1;
  lines.forEach((line, i) => {
    if (line.blank) return;
    if (line.opensParagraph || paragraph < 0) paragraph += 1;
    lastLineOf[paragraph] = i;
  });

  const plan: FigurePlan = new Map();
  for (const image of images) {
    if (!image.placed) continue;
    const at = image.paragraph === TOP ? TOP : lastLineOf[image.paragraph];
    if (at === undefined) continue;
    push(plan, at, image);
  }
  return plan;
}

/**
 * planBySentence maps a sentence index to the pictures that follow it, for a
 * transcript measured against the audio.
 *
 * Matched on the byte offset the server computed rather than on a paragraph
 * count: the transcript is the text as it was *narrated*, and a chapter edited
 * since has a different number of paragraphs. A picture therefore lands after
 * the last sentence that starts before the paragraph it follows ended, which
 * is right while the audio is current and harmlessly approximate once it is
 * not.
 */
export function planBySentence(sentences: Sentence[], images: ChapterImage[]): FigurePlan {
  const plan: FigurePlan = new Map();
  for (const image of images) {
    if (!image.placed) continue;
    if (image.paragraph === TOP) {
      push(plan, TOP, image);
      continue;
    }
    let at = TOP;
    for (let i = 0; i < sentences.length; i++) {
      if (sentences[i]!.offset < image.text_offset) at = i;
      else break;
    }
    push(plan, at, image);
  }
  return plan;
}

function push(plan: FigurePlan, at: number, image: ChapterImage) {
  const existing = plan.get(at);
  if (existing) existing.push(image);
  else plan.set(at, [image]);
}
